import $ from "jquery";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const PAYMENT_TYPES = [
  "Online",
  "DD",
  "Cheque",
  "Damage",
  "Incentive",
  "Rebate",
  "Sales Return",
  "Discount",
  "Settlement",
];

/**
 * The legacy page fed its stock check a hardcoded 500000, so it never fired.
 * Flip this to enforce the real figure from /fg-with-stock instead.
 */
const ENFORCE_STOCK_LIMIT = false;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [product, setProduct] = useState([]);
  const [distributor, setDistributor] = useState([]);
  const [sr, setSr] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Set when a line is marked unavailable, so the user is steered straight to
  // picking a substitute for it.
  const [replacementFor, setReplacementFor] = useState(null);
  const itemsPanelRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    $("body").toggleClass("collapse-menu");
    $("body").toggleClass("show-left");
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [resDemand, resProduct, resDistributor] = await Promise.all([
        Http.get(`/demand/${id}`),
        Http.get("/fg-with-stock"),
        Http.get("/distributor2"),
      ]);

      const demand = resDemand.data;
      setProduct(resProduct.data);
      setDistributor(resDistributor.data);

      const items = demand.list || [];

      // Trade offers are not part of the demand payload, so pull the products
      // the sheet already references alongside it.
      const details = await Promise.all(
        items.map((item) =>
          Http.get(`/product/${item.product_id}`)
            .then((res) => res.data)
            .catch(() => null)
        )
      );

      const rows = items.map((item, index) => {
        const detail = details[index];
        const stockRow = resProduct.data.find((p) => p.id === item.product_id);
        const qty = toNumber(item.qty);
        const delivered = toNumber(item.delivered_qty);

        return {
          id: item.product_id,
          code: item.product ? item.product.code : "",
          name: item.product ? item.product.name : "",
          pack_size: item.packsize,
          stock: stockRow ? toNumber(stockRow.stockQty) : 0,
          trade_offer: detail ? detail.trade_offer || null : null,
          qty: qty,
          rate: toNumber(item.rate),
          to_qty: toNumber(item.invQty) - qty,
          total_qty: toNumber(item.invQty),
          line_total: toNumber(item.price),
          // Already invoiced against this demand: the qty can never drop below it.
          delivered_qty: delivered,
          fully_delivered: delivered > 0 && delivered >= qty,
          is_unavailable: !!item.is_unavailable,
        };
      });

      setList(rows);

      const total = rows.reduce((acc, row) => acc + toNumber(row.line_total), 0);
      const payment = toNumber(demand.payment_amount);
      const previousDue = toNumber(demand.previous_due);

      setInputs({
        receipt_no: demand.receipt_no,
        date: demand.date,
        order_date: demand.order_date,
        time: demand.time,
        distributor_id: demand.distributor_id,
        sr_id: demand.sr_id || "",
        payment_date: demand.payment_date || "",
        payment_amount: demand.payment_amount,
        payment_type: demand.payment_type,
        notes: demand.notes || "",
        previous_due: previousDue,
        total_price: total,
        remaining_amount: payment + previousDue - total,
        due_amount: total - payment,
      });

      if (demand.distributor_id) loadSalesPeople(demand.distributor_id);
    } catch (e) {
      console.error("Fetch error:", e);
      Swal.fire("Error!", e.response?.data?.message || "Failed to load the demand.", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSalesPeople = async (distributorId) => {
    try {
      const res = await Http.get(`/distributor/${distributorId}`);
      const listInfo = [];

      // if (res.data.business_type === "ho") {
        const dataInfo = await Http.get(`/region_area_base_users/${distributorId}`);
        listInfo.push(...dataInfo.data.map((item) => ({ id: item.id, name: item.name })));
      // } else {
      //   for (const key in res.data.sr_base_info) {
      //     const base = res.data.sr_base_info[key].base;
      //     if (base && base.users && base.users.length) {
      //       base.users.forEach((user) => {
      //         if (user.status === "active") listInfo.push({ id: user.id, name: user.name });
      //       });
      //     }
      //   }
      // }
      setSr(listInfo);
      setInputs((values) => ({ ...values, business_type: res.data.business_type }));
    } catch (e) {
      console.error("Distributor fetch error:", e);
    }
  };

  const totalOf = (rows) => rows.reduce((acc, row) => acc + toNumber(row.line_total), 0);

  const isRetail = () => String(inputs.business_type || "").toLowerCase() === "retail";

  /**
   * Retail check, stricter than the create screen: on edit the legacy page
   * allowed no ±500 tolerance at all, the total may not exceed the payment.
   * Returns null when the sheet is fine, otherwise why it is not.
   */
  const retailViolation = (rows, paymentAmount) => {
    if (!isRetail()) return null;

    const total = totalOf(rows);
    const payment = toNumber(paymentAmount);

    if (payment < 1 && total > 0) return "payment-zero";
    if (total > payment) return "exceeds";

    return null;
  };

  /**
   * The largest quantity this line can take without pushing the sheet past the
   * payment amount. Never drops below what has already been delivered.
   */
  const affordableQty = (rows, index) => {
    const others = rows.reduce((acc, row, i) => (i === index ? acc : acc + toNumber(row.line_total)), 0);
    const budget = toNumber(inputs.payment_amount) - others;
    const rate = toNumber(rows[index].rate);
    const affordable = rate > 0 ? Math.floor(budget / rate) : 0;

    return Math.max(affordable, toNumber(rows[index].delivered_qty), 0);
  };

  /**
   * Apply the retail rule to a line the user is editing. Rather than refusing
   * the entry outright, the quantity is clamped to what the payment affords so
   * a replacement item can still be entered.
   */
  const applyRetailRule = (rows, index, fallbackQty) => {
    const violation = retailViolation(rows, inputs.payment_amount);

    if (violation === "payment-zero") {
      Swal.fire("Warning!", "পেমেন্ট এমাউন্ট 0 হতে পারবে না ! Set a Payment Amount first.", "warning");
      rows[index] = buildRow(rows[index], { qty: fallbackQty });
      return rows;
    }

    if (violation === "exceeds") {
      const capped = affordableQty(rows, index);
      rows[index] = buildRow(rows[index], { qty: capped });
      Swal.fire({
        icon: "warning",
        title: "Capped to the payment amount",
        html:
          `This distributor is <b>retail</b>, so the demand total cannot exceed the payment amount ` +
          `(<b>${toNumber(inputs.payment_amount)}</b>).<br/>Quantity set to <b>${capped}</b>. ` +
          `Raise the Payment Amount to demand more.`,
      });
    }

    return rows;
  };

  const recalc = (rows, overrides = {}) => {
    setInputs((values) => {
      const next = { ...values, ...overrides };
      const total = totalOf(rows);
      const payment = toNumber(next.payment_amount);
      const previousDue = toNumber(next.previous_due);

      return {
        ...next,
        total_price: total,
        remaining_amount: payment + previousDue - total,
        due_amount: total - payment,
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    recalc(list, { payment_amount: e.target.value });
  };

  const handleDateChange = async (e) => {
    const { value } = e.target;
    setInputs((values) => ({ ...values, date: value }));

    if (!inputs.distributor_id) return;

    try {
      const due = await Http.get(`/demand-distributor-due/${inputs.distributor_id}`);
      recalc(list, { date: value, previous_due: due.data.previous_due || 0 });
    } catch (e2) {
      console.error("Due fetch error:", e2);
    }
  };

  const selectHandleChange = async (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    const { value } = selectedOption;
    setInputs((values) => ({ ...values, [name]: value }));
    loadSalesPeople(value);

    const due = await Http.get(`/demand-distributor-due/${value}`);
    recalc(list, { previous_due: due.data.previous_due || 0 });
  };

  const tradeOfferQty = (row, qty) => {
    if (!row.trade_offer) return 0;

    const offerAt = toNumber(row.trade_offer.qty);
    const offerQty = toNumber(row.trade_offer.offer_qty);

    if (offerAt <= 0 || offerQty <= 0) return 0;

    return Math.floor(toNumber(qty) / offerAt) * offerQty;
  };

  /**
   * Rebuild one line from a new qty/rate, keeping trade offer and totals in step.
   */
  const buildRow = (row, changes) => {
    const next = { ...row, ...changes };
    next.to_qty = tradeOfferQty(next, next.qty);
    next.total_qty = toNumber(next.qty) + toNumber(next.to_qty);
    next.line_total = toNumber(next.qty) * toNumber(next.rate);
    return next;
  };

  const addMoreList = async (item) => {
    // Both gates come straight from the legacy addProduct handler.
    if (!inputs.distributor_id) {
      Swal.fire("Warning!", "Please select a distributor first.", "warning");
      return;
    }
    if (toNumber(inputs.payment_amount) < 1) {
      Swal.fire("Warning!", "Payment amount should not be 0.", "warning");
      return;
    }

    // Adding a product already on the sheet bumps its quantity instead.
    const existingIndex = list.findIndex((row) => row.id === item.id);
    if (existingIndex > -1) {
      if (list[existingIndex].is_unavailable) {
        Swal.fire("Warning!", "This item is marked unavailable.", "warning");
        return;
      }
      const rows = [...list];
      rows[existingIndex] = buildRow(rows[existingIndex], { qty: toNumber(rows[existingIndex].qty) + 1 });
      setList(rows);
      recalc(rows);
      return;
    }

    try {
      const response = await Http.get(`/product/${item.id}`);
      const p = response.data;
      const stock = toNumber(item.stockQty);

      const row = buildRow(
        {
          id: p.id,
          code: p.code,
          name: p.name,
          pack_size: p.pack_size || 0,
          stock: stock,
          trade_offer: p.trade_offer || null,
          rate: p.price || 0,
          delivered_qty: 0,
          fully_delivered: false,
          is_unavailable: false,
        },
        // A new line starts empty; the legacy qty input was seeded to 0.
        { qty: 0 }
      );

      const rows = [...list, row];
      setList(rows);
      recalc(rows);

      if (replacementFor) {
        setReplacementFor(null);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `${row.name} added \u2014 set its quantity`,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (e) {
      Swal.fire("Error!", "Failed to add product.", "error");
    }
  };

  const removeList = (index) => {
    // Dropping a line that has already been invoiced would orphan the order it
    // was delivered against, so it stays.
    if (toNumber(list[index].delivered_qty) > 0) {
      Swal.fire("Not allowed!", "Part of this item has already been delivered, so it cannot be removed.", "warning");
      return;
    }
    const rows = list.filter((_, i) => i !== index);
    setList(rows);
    recalc(rows);
  };

  const handleQtyChange = (e, index) => {
    const { value } = e.target;
    const rows = [...list];
    const row = rows[index];
    let qty = value;

    if (toNumber(qty) < toNumber(row.delivered_qty)) {
      Swal.fire("Not allowed!", `Already delivered ${row.delivered_qty} of this item.`, "warning");
      qty = row.delivered_qty;
    } else if (ENFORCE_STOCK_LIMIT && toNumber(qty) > toNumber(row.stock)) {
      Swal.fire("Warning!", `You have ${row.stock} items left in the stock.`, "warning");
      qty = row.stock;
    }

    rows[index] = buildRow(row, { qty });

    // Checked against what the sheet would become, then clamped rather than
    // rejected, so a replacement line can still be filled in.
    applyRetailRule(rows, index, row.delivered_qty);

    setList(rows);
    recalc(rows);
  };

  const handleRateChange = (e, index) => {
    const rows = [...list];
    const row = rows[index];
    rows[index] = buildRow(row, { rate: e.target.value });

    applyRetailRule(rows, index, row.delivered_qty);

    setList(rows);
    recalc(rows);
  };

  /**
   * Marking a line unavailable drops it back to whatever was already delivered
   * and locks the row; clearing it hands the row back to the user.
   */
  const handleUnavailableChange = (e, index) => {
    const checked = e.target.checked;
    const rows = [...list];
    const name = rows[index].name;

    rows[index] = buildRow(rows[index], {
      is_unavailable: checked,
      qty: checked ? rows[index].delivered_qty : rows[index].qty,
    });

    setList(rows);
    recalc(rows);

    if (checked) {
      // Freeing the line leaves budget for a substitute, so ask for one now.
      setReplacementFor(name);
      if (itemsPanelRef.current) {
        itemsPanelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: `Add a replacement for ${name}`,
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
      });
    } else if (replacementFor === name) {
      setReplacementFor(null);
    }
  };

  // Digits and a single decimal point only, like the legacy numOnly binding.
  const numOnly = (e) => {
    if (!/[0-9.]/.test(e.key) || (e.key === "." && String(e.target.value).includes("."))) {
      e.preventDefault();
    }
  };

  const formatOnBlur = (index, field) => {
    const rows = [...list];
    rows[index][field] = toNumber(rows[index][field]).toFixed(2);
    setList(rows);
  };

  const formSubmit = () => {
    if (list.length < 1) {
      Swal.fire("Warning!", "Please add a few products to process the demand.", "warning");
      return;
    }

    const total = totalOf(list);
    const payment = toNumber(inputs.payment_amount);

    // The ±500 window is checked for every distributor on submit.
    if (total < payment - 500 || total > payment + 500) {
      Swal.fire("Invalid!", "Total price is not within the payment amount ±500.", "warning");
      return;
    }

    const payload = {
      ...inputs,
      total_price: total,
      due_amount: total - payment,
      items: list.map((row) => ({
        id: row.id,
        pack_size: row.pack_size,
        qty: row.qty,
        inv_qty: row.total_qty,
        rate: row.rate,
        is_unavailable: row.is_unavailable,
      })),
    };

    Http.put(`/demand/${id}`, payload)
      .then((res) => {
        navigate("/demand-sheet", { state: { message: res.data.message, type: "success" } });
      })
      .catch((e) => {
        setError(e.response?.data?.errors || {});
        if (e.response?.data?.message && !e.response?.data?.errors) {
          Swal.fire("Failed!", e.response.data.message, "error");
        }
      });
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const cols = [
    { field: "name", header: "Item" },
    { field: "stockQty", header: "Stock" },
  ];

  const actionTemplate = (item) => (
    <td className="text-right">
      <button className="btn btn-success btn-sm" onClick={() => addMoreList(item)}>
        <i className="fa fa-plus"></i>
      </button>
    </td>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <style>{`.full-border td, .full-border th { border: 1px solid red; }`}</style>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Modify Demand Sheet</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-8">
            <h6 className="card-body-title mg-sm-b-30 text-center">Modify Demand</h6>
            <hr />

            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Demand No <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="receipt_no"
                      value={inputs.receipt_no || ""}
                      onChange={handleChange}
                    />
                    {displayError("receipt_no")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Demand Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleDateChange}
                    />
                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>Order Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="order_date"
                      value={inputs.order_date || ""}
                      onChange={handleChange}
                    />
                    {displayError("order_date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input type="time" className="form-control" name="time" value={inputs.time || ""} onChange={handleChange} />
                    {displayError("time")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Distributor <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="distributor_id"
                      options={distributor}
                      value={distributor.find((d) => d.value === inputs.distributor_id) || null}
                      onChange={selectHandleChange}
                    />
                    {displayError("distributor_id")}
                  </div>
                  <div className="col-lg">
                    <label>Sales Person</label>
                    <select className="form-control" name="sr_id" value={inputs.sr_id || ""} onChange={handleChange}>
                      <option value="">Select SR</option>
                      {Array.from(sr).map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {displayError("sr_id")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="payment_date"
                      value={inputs.payment_date || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg">
                    <label>
                      Payment Amount <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="payment_amount"
                      value={inputs.payment_amount || ""}
                      onChange={handlePaymentChange}
                      onKeyPress={numOnly}
                    />
                    {displayError("payment_amount")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Payment Type <i className="fa fa-star required"></i>
                    </label>
                    <select
                      className="form-control"
                      name="payment_type"
                      value={inputs.payment_type || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Payment Type</option>
                      {PAYMENT_TYPES.map((type) => (
                        <option value={type} key={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {displayError("payment_type")}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Notes</label>
                    <textarea className="form-control" name="notes" value={inputs.notes || ""} onChange={handleChange} />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="card-solid">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">Items in the Demand</h5>
                    </div>
                    <div className="card-body p-0">
                      <table className="table table-sm table-bordered table-striped mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Code</th>
                            <th>Product</th>
                            <th>Crtn Size</th>
                            <th>Qty</th>
                            <th>Delivered</th>
                            <th>Rate</th>
                            <th>Total Qty</th>
                            <th>Line Total</th>
                            <th>Unavailable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.length < 1 && (
                            <tr>
                              <td colSpan={10}>
                                <p style={{ color: "red" }} className="text-center mt-3">
                                  Please add a few products to process the demand
                                </p>
                              </td>
                            </tr>
                          )}

                          {list.map((x, i) => (
                            // Legacy marked an unavailable line by bordering its cells red.
                            <tr key={x.id} className={x.is_unavailable ? "full-border" : ""}>
                              <td>
                                {toNumber(x.delivered_qty) > 0 ? (
                                  <button className="btn btn-danger btn-sm" disabled title="Already delivered">
                                    <i className="fa fa-trash"></i>
                                  </button>
                                ) : (
                                  <button className="btn btn-danger btn-sm" onClick={() => removeList(i)}>
                                    <i className="fa fa-trash"></i>
                                  </button>
                                )}
                              </td>
                              <td>{x.code}</td>
                              <td>{x.name}</td>
                              <td>{x.pack_size}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="qty"
                                  value={x.qty}
                                  min={x.delivered_qty}
                                  readOnly={x.is_unavailable}
                                  onChange={(e) => handleQtyChange(e, i)}
                                  onKeyPress={numOnly}
                                  onBlur={() => formatOnBlur(i, "qty")}
                                />
                                {displayError("items." + i + ".qty")}
                              </td>
                              <td className="text-danger">
                                {toNumber(x.delivered_qty) > 0 ? <b>{x.delivered_qty}</b> : 0}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control"
                                  name="rate"
                                  value={x.rate}
                                  // Locked once the line is fully delivered or unavailable.
                                  readOnly={x.is_unavailable || x.fully_delivered}
                                  onChange={(e) => handleRateChange(e, i)}
                                  onKeyPress={numOnly}
                                  onBlur={() => formatOnBlur(i, "rate")}
                                />
                                {displayError("items." + i + ".rate")}
                              </td>
                              <td>
                                <input type="number" className="form-control" value={x.total_qty || 0} disabled />
                              </td>
                              <td>
                                <input type="number" className="form-control" value={x.line_total || 0} disabled />
                              </td>
                              <td className="text-center">
                                {/* Only offered while something is still outstanding. */}
                                {!x.fully_delivered && (
                                  <input
                                    type="checkbox"
                                    checked={x.is_unavailable}
                                    onChange={(e) => handleUnavailableChange(e, i)}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={8} className="text-right" style={{ lineHeight: "40px" }}>
                              Total Price
                            </td>
                            <td colSpan={2}>
                              <input type="text" className="form-control" value={inputs.total_price || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={8} className="text-right" style={{ lineHeight: "40px" }}>
                              Previous Due
                            </td>
                            <td colSpan={2}>
                              <input type="text" className="form-control" value={inputs.previous_due || 0} disabled />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={8} className="text-right" style={{ lineHeight: "40px" }}>
                              Remaining Amount
                            </td>
                            <td colSpan={2}>
                              <input type="text" className="form-control" value={inputs.remaining_amount || 0} disabled />
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button className="btn btn-primary btn-block mg-b-10" onClick={formSubmit}>
                      <i className="fa fa-send mg-r-10"></i> Update Demand
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            ref={itemsPanelRef}
            className="card pd-20 pd-sm-40 col-3"
            style={{
              backgroundColor: "#ffffffc7",
              border: replacementFor ? "2px solid #e42320" : undefined,
            }}
          >
            <h6 className="card-body-title mg-sm-b-30 text-center">Available Items</h6>
            {replacementFor && (
              <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: "13px" }}>
                <i className="fa fa-exchange mg-r-6"></i>
                Pick a replacement for <b>{replacementFor}</b>.
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-2"
                  onClick={() => setReplacementFor(null)}
                >
                  Dismiss
                </button>
              </div>
            )}
            <hr />
            <ReactDataTable cols={cols} receiveData={product} isLoading={loading} actionTemplate={actionTemplate} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edit;
